// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/argent.js — LES LEVIERS DE L'ARGENT (Données, Facturation,
// Historique, Compta).
//
// ══ POURQUOI CETTE FAMILLE EXISTE, ET POURQUOI SI TARD ═════════════════════
//
// Le sujet `argent` était le seul sujet SANS levier (`_forme.js`,
// `SUJETS_SANS_LEVIER`, 2026-09-15) : les quatre écrans de l'argent postent
// un document entier ou écrivent depuis le navigateur — rien à composer sans
// « monter à la va-vite » un geste sur le chemin argent. La question était
// posée (cadrage § 7, Q1).
//
// Dimitri a répondu le 2026-09-15, en deux phrases qui bornent tout :
//
//   « Renvoyer une facture déjà émise : oui, vague 9. Le destinataire lu dans
//     la fiche, jamais saisi. Émettre reste interdit. »
//   « Poser le dernier numéro d'une série : interdit absolu. Confirmé. »
//
// La seconde vit dans `INTERDITS_ABSOLUS` (`setInvoiceSequence`). La première
// est le seul levier de cette famille.
//
// ⚠️ LE « ÉMETTRE RESTE INTERDIT » DU 2026-09-15 A ÉTÉ REVU LE 2026-10-01.
// Dimitri : « émettre une facture neuve = validation à la main » ; « renvoyer
// une facture déjà émise = carte à valider » (renvoyer la même pièce n'est pas
// la toucher — `facture_renvoyer` ci-dessous ne change donc pas de niveau).
// `setInvoiceDelivered` est descendu dans ECARTES (`argent_sous_gardes`) ; ses
// gardes y sont le cahier des charges du levier, qui n'existe pas encore.
//
// ══ CE QUE CETTE FAMILLE NE CONTIENDRA PAS ═════════════════════════════════
//
//   · ÉMETTRE une facture À LA VOIX (`setInvoiceDelivered` = attribution du
//     numéro, CGI 289) — écarté sous gardes, validation à la main seulement ;
//   · SUPPRIMER une pièce (`deleteInvoiceHard`, interdit absolu) ;
//   · CALER un compteur (`setInvoiceSequence`, interdit absolu) ;
//   · SAISIR une adresse : le destinataire vient de la fiche du propriétaire
//     ou de la réservation, LU côté serveur. L'intention ne porte que
//     l'identifiant de la pièce.

// ══ « vise=logement » AU CATALOGUE — LE NOM DIT, PAS L'IDENTIFIANT ═════════
//
// Les leviers de cette famille qui portent `args_resolus` ne montrent plus
// `logement_id` au modèle : il pose « Cocon Naturel » dans `vise`, et le
// serveur retrouve `lc1` au moment de composer la carte
// (`lib/reperes-geste.js`). L'argument RESTE dans `args_autorises` — l'en
// retirer ferait refuser la carte au clic (`filtrerArgs`, rejoué par le
// navigateur) et casserait les aperçus qui le citent en `depend_de`.
//
// ⚠️ ET SI LE MODÈLE LE COMPOSE QUAND MÊME, LA CARTE NE VIENT PAS. On ne lui
// demande pas de s'abstenir : on rend la chose sans effet, et l'écran demande
// à Dimitri de nommer le logement. « Cocon » tout seul désigne DEUX logements
// aux commissions différentes — ce n'est pas un détail de confort.
const LEVIERS_ARGENT = Object.freeze({
  // ══ L'ÉCRAN COMPTA — TROIS GESTES SUR DEUX SORTES DE LIGNES (2026-09-17) ══
  //
  // Dimitri a nommé la compta. L'écran n'avait AUCUNE porte : ses quatre
  // boutons recomposent le blob entier en mémoire et le POSTent — un seul
  // bouton côté serveur, « enregistre tout ».
  //
  // C'est devenu adressable parce que le LOT 0 a posé `x-compta-version` sur
  // `/api/compta` : sans ce jeton, deux écrivains (l'écran et le geste)
  // s'écrasent en silence, dernier arrivé gagnant.
  //
  // ⚠️ TROIS LEVIERS ET PAS UN, POUR LE MOTIF HABITUEL DU DÉPÔT : un marquage
  // et un `sans_retour` portent sur un levier ENTIER. Ajouter une ligne ne
  // détruit rien ; en retirer une est sans retour. Une seule carte aurait dû
  // afficher « rien ne se perd » ET « c'est définitif ».
  //
  // ⚠️ DEUX SORTES DE LIGNES, ET LEUR DIFFÉRENCE N'EST PAS COSMÉTIQUE. La
  // CHARGE FIXE (abonnement, assurance, comptable) n'a PAS DE MOIS : elle court
  // tous les mois. La DÉPENSE en a un. C'est pourquoi `mois` est déclaré
  // indispensable nulle part : il dépend de la sorte, et le serveur le réclame
  // franchement quand il manque.
  //
  // ⚠️ ET LE MOIS DÉJÀ FACTURÉ NE BLOQUE PAS — CE N'EST PAS UN OUBLI. Vérifié
  // sur le code le 2026-09-17 : le blob compta n'est lu QUE par l'écran Compta,
  // par le bandeau « marge nette » de l'Accueil et par les lectures de
  // l'opérateur. Aucune facture, ni propriétaire ni voyageur, ne l'ouvre. Une
  // ligne de compta ne peut donc pas faire diverger une facture — et refuser
  // sur facture émise empêcherait Dimitri d'enregistrer sa propre essence sur
  // un mois dont la facture du propriétaire est partie. Ce qui reste et qui
  // s'applique : le PLANCHER de l'application, refusé franchement, avec sa date.
  //
  // ⚠️ CE QUE CES TROIS LEVIERS NE FONT PAS, ET NE FERONT PAS :
  //   · L'IMPORT DU RELEVÉ (bouton « CSV Shine ») — un fichier ne se dicte
  //     pas. L'écran Compta est déjà une destination connue de la carte de
  //     l'app (`lib/app-carte.js`) : l'opérateur y EMMÈNE, il n'y a rien à
  //     monter pour cela.
  //   · LE VOLET CONSOMMABLES — il n'existe pas. `compta.consommables` est un
  //     CHAMP MORT depuis la refonte du 2026-04-29 (« jamais écrit → 0 »,
  //     `ComptaTab.jsx`), et il vaut `{}` en production. Le coût affiché à
  //     l'écran est sommé des DÉPÔTS de stock, pas de ce blob. Il n'y a pas de
  //     bouton derrière, donc pas de porte à ouvrir.

  // ── AJOUTER ──────────────────────────────────────────────────────────────
  //
  // ⚠️ L'IDENTIFIANT N'EST PAS UN ARGUMENT, et c'est une CORRECTION. L'écran
  // pose `id: null` sur une dépense neuve (`DvModal`), et `saveDV` retrouve
  // ensuite la ligne par son identifiant : deux dépenses créées à la suite
  // portent toutes deux `null`, et la seconde ÉCRASE la première, sans erreur
  // et sans message. Le défaut n'a jamais mordu parce qu'aucune dépense
  // variable n'a jamais été saisie en production. Le serveur tire donc
  // l'identifiant lui-même et le vérifie unique — il ne l'accepte jamais de
  // l'appelant. (Le défaut de l'écran est signalé, pas corrigé ici.)
  compta_ligne_ajouter: {
    ecran: "compta",
    endpoint: "/api/beds24-mapping",
    action: "compta_ligne_ajouter",
    methode: "POST",
    args_autorises: [
      "sorte",
      "mois",
      "libelle",
      "montant",
      "logement",
      "paye_par",
      "date",
    ],
    args_valeurs: {
      sorte: ["charge_fixe", "depense_variable"],
      paye_par: ["shine", "neisia", "dimitri", "cash"],
    },
    args_libres: ["libelle"],
    // ⚠️ LE LOGEMENT SE RÉSOUT, IL NE SE COMPOSE PAS (lot 6, revue adverse M2
    // — chemin argent). Le filet serveur (`logementsConnus()`) refuse un
    // identifiant INCONNU, jamais un identifiant valide et FAUX : « la lessive
    // du Cocon » avec `logement:"l5"` (Cocon Toulousain) au lieu de lc1
    // passait, sur de l'argent. La garde du composé ne s'arme que sur les
    // clés d'`args_resolus` — d'où cette déclaration.
    // ⚠️ `requis: []` : une dépense SANS logement est parfaitement légitime
    // (« Global (tous logements) » est le défaut du sélecteur à l'écran). Le
    // repère est facultatif ; la garde du composé, elle, s'arme quand même.
    args_resolus: {
      logement: { objet: "logement", par: ["logement"], requis: [] },
    },
    // ⚠️ `montant` N'EST PLUS INDISPENSABLE : IL SE TAPE (lot 6, revue adverse
    // M1 — chemin argent). Le modèle l'entendait (« 42 euros d'essence ») et
    // le composait ; sans lui, `manqueIndispensable` grisait la carte avant
    // qu'on puisse le taper. Désormais : retiré s'il est composé, champ sur la
    // carte, bouton gris tant qu'il est vide — et le registre REFUSE qu'un
    // chemin soit à la fois saisi et indispensable (`_forme.js`).
    args_indispensables: ["sorte", "libelle"],
    args_saisis: {
      montant: { libelle: "le montant, en euros", nature: "montant" },
    },
    apercu: {
      lecture: "compta_ligne_lire",
      depend_de: ["sorte", "mois"],
      jeton: "x-compta-version",
    },
    titre: "Ajouter une charge ou une dépense à la compta",
    constatable:
      "la ligne apparaît dans Argent › Compta avec son montant — une charge fixe dans la liste du haut (elle comptera TOUS les mois, c'est le principe d'un abonnement), une dépense dans le mois que vous avez dit. Le total des charges et le résultat net du mois suivent aussitôt, à l'écran comme sur le graphique de l'année. Rien n'apparaît sur la facture d'un propriétaire : c'est votre comptabilité à vous.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    ne_fait_pas:
      "il ne paie rien et ne rembourse personne : il note une dépense, il ne la règle pas. Il ne touche aucune facture, ni émise ni à venir.",
    dit_comme: [
      "Ajoute une charge fixe : assurance, 125 euros par mois",
      "Note une dépense de 42 euros d'essence en septembre",
      "J'ai un nouvel abonnement à 29 euros, ajoute-le aux charges",
      "Mets 80 euros de plombier sur le mois dernier",
    ],
    marquages: ["argent"],
    vague: 10,
    motif_requis: false,
    autonome: false,
    sans_retour: null, // une ligne s'ajoute, et se retire
    // E1 ✔ (rien n'est détruit ; la ligne se retire) · E2 ✔ · E3 ✔ (même
    // magasin que le bouton Enregistrer de l'écran, jeton de version, diff
    // intégral) · E4 ✔ · E5 ✔.
  },

  // ── CORRIGER UN CHAMP ────────────────────────────────────────────────────
  compta_ligne_champ_set: {
    ecran: "compta",
    endpoint: "/api/beds24-mapping",
    action: "compta_ligne_champ_set",
    methode: "POST",
    args_autorises: ["sorte", "mois", "ligne_id", "champ", "nouvelle"],
    args_valeurs: {
      sorte: ["charge_fixe", "depense_variable"],
      champ: ["libelle", "montant", "logement", "paye_par", "date"],
    },
    args_libres: ["nouvelle"],
    // ⚠️ `nouvelle` SE TAPE QUAND C'EST UN MONTANT, ET SEULEMENT ALORS (lot 6,
    // M1). « La Maif est passée à 130 euros » : 130 est un chiffre d'argent,
    // il ne se compose pas. « Corrige le libellé du comptable » : le libellé,
    // lui, se compose — et il reste exigé, par la MÊME déclaration
    // (`manqueIndispensable` réclame une saisie hors de sa condition). D'où
    // `si: { champ: ["montant"] }`, et `nouvelle` hors d'`args_indispensables`.
    // ⚠️ LA LIGNE SE RÉSOUT, ET ELLE POSE SA SORTE ET SON MOIS (lot 6).
    // `ligne_id` est tiré par le serveur (sept caractères de base 36) :
    // personne ne l'a jamais vu. Et la SORTE ne se devine pas — elle se déduit
    // de l'endroit où la ligne vit : une charge fixe n'a pas de mois, une
    // dépense en a un. Laisser le modèle poser « charge_fixe » sur une dépense
    // rendait la ligne introuvable — ou pire, désignait son homonyme.
    // L'ordre de déclaration EST l'ordre de résolution : l'objet est lu une
    // fois, et ses trois arguments en sortent ensemble.
    args_resolus: {
      ligne_id: {
        objet: "ligne_compta",
        par: ["nom", "quand", "logement"],
      },
      sorte: {
        objet: "ligne_compta",
        par: ["nom", "quand", "logement"],
        champ: "sorte",
      },
      mois: {
        objet: "ligne_compta",
        par: ["nom", "quand", "logement"],
        champ: "mois",
        requis: [],
      },
    },
    args_indispensables: ["sorte", "ligne_id", "champ"],
    args_saisis: {
      nouvelle: {
        libelle: "le montant corrigé, en euros",
        nature: "montant",
        si: { champ: ["montant"] },
      },
    },
    apercu: {
      lecture: "compta_ligne_lire",
      depend_de: ["sorte", "mois", "ligne_id"],
      jeton: "x-compta-version",
    },
    titre: "Corriger une ligne de la compta",
    constatable:
      "la ligne porte sa nouvelle valeur dans Argent › Compta, et le total du mois suit. La carte vous montre l'ancienne valeur et la nouvelle avant le clic. Un champ à la fois : le libellé, le montant, et pour une dépense le logement concerné, qui a payé, ou la date. Rien n'apparaît sur la facture d'un propriétaire.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    ne_fait_pas:
      "il ne change pas la SORTE d'une ligne : une dépense ne devient pas une charge fixe, et l'inverse non plus. Il ne déplace pas une dépense d'un mois à l'autre.",
    dit_comme: [
      "La Maif est passée à 130 euros, corrige-la",
      "Passe l'abonnement Melo à 30 € par mois",
      "Cette dépense d'essence, c'était pour le Cocon en fait",
      "Corrige le libellé de la charge du comptable",
    ],
    marquages: ["argent"],
    vague: 4,
    motif_requis: false,
    autonome: false,
    sans_retour:
      "l'ancienne valeur n'est gardée que dans le journal : la carte vous la montre, mais il faudra la retaper pour revenir en arrière.",
    // E1 ✔ · E2 ✔ · E3 ✔ · E4 ✔ · E5 ✔.
  },

  // ── RETIRER ──────────────────────────────────────────────────────────────
  //
  // ⚠️ VAGUE 4 ET NON 2. « Rangement et clôtures » (vague 2) est faite pour ce
  // qui se défait — un retrait de réservation se remet d'un geste. Ici rien
  // n'est mis de côté : la ligne part, et son montant avec. Une charge fixe à
  // 600 € retirée par erreur, ce sont 600 € qui disparaissent du calcul chaque
  // mois jusqu'à ce que quelqu'un s'en aperçoive.
  compta_ligne_retirer: {
    ecran: "compta",
    endpoint: "/api/beds24-mapping",
    action: "compta_ligne_retirer",
    methode: "POST",
    args_autorises: ["sorte", "mois", "ligne_id"],
    args_valeurs: { sorte: ["charge_fixe", "depense_variable"] },
    // Même déclaration que la correction, même raison (lot 6).
    args_resolus: {
      ligne_id: { objet: "ligne_compta", par: ["nom", "quand", "logement"] },
      sorte: {
        objet: "ligne_compta",
        par: ["nom", "quand", "logement"],
        champ: "sorte",
      },
      mois: {
        objet: "ligne_compta",
        par: ["nom", "quand", "logement"],
        champ: "mois",
        requis: [],
      },
    },
    args_indispensables: ["sorte", "ligne_id"],
    apercu: {
      lecture: "compta_ligne_lire",
      depend_de: ["sorte", "mois", "ligne_id"],
      jeton: "x-compta-version",
    },
    titre: "Retirer une ligne de la compta",
    constatable:
      "la ligne disparaît d'Argent › Compta et son montant sort du total : pour une charge fixe, de TOUS les mois d'un coup, puisqu'une charge fixe court tous les mois. La carte vous montre le libellé et le montant exact avant le clic. Rien ne change sur aucune facture.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    ne_fait_pas:
      "il n'annule aucun paiement et ne rembourse rien : la dépense a eu lieu, il cesse seulement de la compter.",
    dit_comme: [
      "Retire la charge Sunver, je ne l'ai plus",
      "Supprime cette dépense, je l'ai saisie deux fois",
      "Enlève l'abonnement Pricelabs des charges fixes",
    ],
    marquages: ["argent", "supprime"],
    vague: 4,
    motif_requis: false,
    autonome: false,
    sans_retour:
      "la ligne retirée n'est mise de côté nulle part : il faudra la retaper, avec son libellé et son montant.",
    // E1 ✔ (le marquage « supprime » le dit, et la carte montre la ligne
    // entière) · E2 ✔ · E3 ✔ · E4 ✔ · E5 ✔.
  },

  // ══ « RENVOIE LA FACTURE DE JUILLET À PIERRE » (2026-09-15, vague 9) ═══════
  //
  // Action `facture_renvoyer` (lib/factures-envoi-handlers.js), écrite pour
  // l'occasion : il n'existait AUCUN envoi serveur d'une facture (l'écran
  // passe par EmailJS côté navigateur, le cron des reçus n'envoie plus rien).
  // Elle envoie le document ARCHIVÉ (`html_snapshot`), jamais régénéré.
  //
  // ⚠️ VAGUE 9, PAR DÉCISION DIMITRI — pas parce qu'un texte est à écrire
  // (il n'y en a pas), mais parce qu'un e-mail part chez un tiers avec une
  // pièce comptable : « le plus sensible en dernier ».
  //
  // ⚠️ UN SEUL ARGUMENT, ET C'EST TOUTE LA DÉCISION. `invoice_id` désigne la
  // pièce ; le destinataire est LU par le serveur (séries P et LEGACY :
  // e-mail du propriétaire dans sa fiche ; série V : e-mail de la
  // réservation ; toute autre série : refus, jamais un repli).
  // Pas d'adresse enregistrée ⇒ refus franc, jamais une saisie. La sentinelle
  // des arguments refuse d'ailleurs tout `email`/`to`/`destinataire`.
  //
  // ⚠️ LA CARTE LIT AVANT LE CLIC (`facture_lire`) : numéro, mois, montant,
  // propriétaire, destinataire MASQUÉ, date d'émission, date de transmission —
  // et rend un jeton dérivé de ces faits ET du destinataire. Si l'adresse du
  // propriétaire change dans la fiche entre l'affichage et le clic, la porte
  // refuse : Dimitri validerait un envoi vers une adresse qu'il n'a pas vue.
  facture_renvoyer: {
    ecran: "historique",
    endpoint: "/api/beds24-mapping",
    action: "facture_renvoyer",
    methode: "POST",
    args_autorises: ["invoice_id"],
    // ⚠️ ET CET ARGUMENT NE SE COMPOSE PLUS (lot 6). `invoice_id` est un uuid :
    // Dimitri ne le prononce jamais, et le modèle ne l'a jamais lu — la
    // lecture `factures_emises` rend des NUMÉROS. S'il en posait un, il
    // l'avait inventé, et derrière c'est un e-mail RÉEL chez un propriétaire
    // avec la pièce comptable d'un autre. Désormais il se RÉSOUT : le logement
    // (ou son propriétaire — « Bruno »), le mois, le rang (« la dernière »),
    // lus dans `invoices` avec le jeton de Dimitri. La SÉRIE, elle, se lit
    // dans sa phrase : « la facture » (P/LEGACY), « le reçu » (V), « un
    // avoir ». Résolveur : `lib/facture-repere.js`.
    args_resolus: {
      invoice_id: { objet: "facture", par: ["logement", "quand", "rang"] },
    },
    apercu: {
      lecture: "facture_lire",
      depend_de: ["invoice_id"],
      // `x-apercu-version`, PAS `x-cfg-version` : ce geste n'écrit pas le
      // blob de configuration. Le jeton est dérivé du numéro, du statut, de
      // l'annulation, de la transmission et du DESTINATAIRE.
      jeton: "x-apercu-version",
    },
    titre: "Renvoyer une facture déjà émise à son destinataire",
    constatable:
      "un e-mail PART chez le destinataire lu dans la fiche du propriétaire (ou dans la réservation, pour un reçu voyageur) avec le document tel qu'il a été émis — le même que « Voir la facture » dans Argent › Historique. La pièce n'est PAS modifiée (une facture émise ne se touche pas) : sa date de transmission reste celle de l'émission, et le renvoi est tracé dans le journal. En production l'envoi est RÉEL (c'est le cas courant) : l'e-mail part vraiment, et la carte nomme le destinataire avant le clic. Le mode simulation est l'exception (preview, local) ; quand il est actif, rien ne part et la carte le dit à la place. Le geste est refusé si la pièce n'a pas de numéro, si elle a été annulée, si sa série est inconnue, si la fiche a changé de propriétaire depuis l'émission, ou si aucune adresse n'est enregistrée.",
    sujet: "argent",
    cout: ["envoi"],
    // Séries P / LEGACY : le propriétaire. Série V : le voyageur de la
    // réservation. Les deux sont des destinataires de message, et le marquage
    // « sortant » va avec.
    ou_ca_part: ["proprietaire", "voyageur"],
    ne_fait_pas:
      "il n'émet rien, ne modifie ni le numéro, ni le montant, ni la date, ni le document ; il n'envoie jamais à une adresse tapée, et il ne renvoie pas un avoir (l'avoir a son propre envoi depuis Argent › Historique).",
    // ⚠️ RÉÉCRITS SUR DES FAITS MESURÉS (lot 6, revue M8). Les trois exemples
    // d'origine étaient INJOUABLES en production : aucun « Pierre » parmi les
    // sept propriétaires, et août 2026 n'a AUCUNE facture dans l'app (mois
    // facturé à la main, hors app). Un exemple qui échoue sur la phrase réelle
    // apprend au modèle une phrase que rien ne sait résoudre — c'est le motif
    // déjà vu sur #1433 et #1434.
    dit_comme: [
      "Renvoie la facture de juillet à Bruno",
      "Le Havre de Bruguières n'a pas reçu sa facture de juillet, renvoie-la",
      "Renvoie la dernière facture de Fanny",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
    // ⚠️ EN PROD `MESSAGES_DRY_RUN` VAUT 'false' (Vercel, constaté le
    // 2026-09-16) : le cas RÉEL est le cas COURANT, pas une hypothèse. La
    // phrase vaut donc pleinement à chaque clic en production.
    sans_retour:
      "Un e-mail parti ne se rappelle pas — et en production c'est bien un e-mail réel qui part, pas une simulation.",
    // E1 ✔ (rien n'est détruit : un e-mail part, et c'est le marquage
    // « sortant » qui le dit ; la pièce n'est pas réécrite) · E2 ✔ (le journal
    // porte le renvoi) · E3 ✔ (ZÉRO écriture sur `invoices` — décision Dimitri
    // 2026-09-16, « une pièce émise ne se touche pas » ; le bouton « envoyé »
    // de l'écran reste le seul écrivain de la transmission ET du numéro) ·
    // E4 ✔ (aucun fait attesté : renvoyer n'affirme pas que le destinataire a
    // reçu) · E5 ✔.
  },

  // ══ LES CORRECTIONS D'UN MOIS DE FACTURATION (2026-09-17, vague 6) ════════
  //
  // Dimitri : « commence par ce que j'utilise : retirer ou remettre une
  // réservation, les ajustements. »
  //
  // ⚠️ CES QUATRE GESTES NE TOUCHENT AUCUNE FACTURE ÉMISE, et c'est ce qui les
  // rend possibles. Ils corrigent le mois AVANT qu'il ne soit facturé ; dès
  // qu'une facture de commission ou une facture voyageur existe pour ce mois,
  // les quatre refusent en nommant la pièce (lib/facturation-gel.js). Émettre,
  // renuméroter, supprimer une pièce restent des interdits absolus.
  //
  // ⚠️ VAGUE 6 — NEUVE, ET FERMÉE. Les vagues 1 à 4 et 9 sont ouvertes en
  // production : y ranger l'un de ces gestes l'aurait ouvert le jour de sa
  // fusion, alors qu'aucun d'eux n'a encore servi une seule fois. Ouvrir la
  // vague 6 est un geste de Dimitri, pas une ligne de code.

  // ── « RETIRE CETTE RÉSERVATION DE SEPTEMBRE » ─────────────────────────────
  //
  // Le calque de retrait (ADR-212). La réservation cesse d'entrer dans la
  // facture du mois ; elle reste affichée, barrée, avec son bouton de retour.
  // Rien n'est effacé, ni chez nous ni chez Beds24 — c'est pour cela que le
  // geste inverse existe, juste en dessous, et qu'il a le même rang.
  data_resa_retirer: {
    ecran: "donnees",
    endpoint: "/api/beds24-mapping",
    action: "data_resa_retirer",
    methode: "POST",
    args_autorises: ["logement_id", "mois", "code"],
    // ⚠️ LA RÉSERVATION SE RÉSOUT, ET ELLE POSE SES TROIS ARGUMENTS (lot 6).
    // `code` est la référence de la plateforme (« HMQKTF3F5D ») : Dimitri ne
    // la prononce jamais, et le modèle ne l'a jamais lue. Elle ressemble
    // pourtant à quelque chose qu'on peut inventer — c'est ce qui la rend
    // dangereuse : un code composé retire une AUTRE réservation de la facture
    // du mois. `logement_id` et `mois` sortent du même séjour résolu ; le mois
    // est celui de la FACTURATION (calculé aux nuits), pas celui de l'arrivée.
    args_resolus: {
      code: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
      },
      logement_id: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
        champ: "logement_id",
      },
      mois: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
        champ: "mois",
      },
    },
    apercu: {
      lecture: "data_resa_lire",
      depend_de: ["logement_id", "mois", "code"],
      champ: "retiree",
      // Le jeton du blob de facturation, celui-là même que l'écran Données
      // présente quand il enregistre. Si un chiffre du mois bouge entre
      // l'affichage et le clic, la porte refuse.
      jeton: "x-data-version",
    },
    titre: "Retirer une réservation de la facturation",
    constatable:
      "la réservation reste affichée dans Argent › Données, barrée, avec son bouton de retour — et elle cesse de compter dans le total du mois : le montant du propriétaire et la commission baissent d'autant sur sa prochaine facture. Rien n'est effacé : ni la réservation, ni ce qu'elle porte, ni quoi que ce soit chez Beds24. Le geste est refusé si le mois est déjà facturé (la pièce est nommée), si la réservation est déjà retirée, si le logement n'est pas en facturation automatique, ou si le montant de la réservation est réparti sur plusieurs mois — dans ce dernier cas le geste dit que la réservation existe et que sa répartition se règle sur l'écran, il ne prétend jamais qu'elle n'existe pas.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il n'annule pas le séjour, ne prévient personne, ne touche ni Beds24 ni les plateformes, et n'efface aucune ligne : la réservation reste là, elle ne compte plus.",
    dit_comme: [
      "Retire cette réservation de septembre",
      "Celle-là ne doit pas être facturée",
      "Retire-la du mois",
    ],
    marquages: ["argent", "sortant", "supprime"],
    vague: 6,
    motif_requis: false,
    autonome: false,
    sans_retour: null,
    // E1 ✔ (retrait réversible, le geste de retour est déclaré juste en
    // dessous) · E2 ✔ (la ligne se barre, le total du mois change) · E3 ✔ (ce
    // calque n'a qu'un écrivain : l'écran Données) · E4 ✔ (aucun fait attesté)
    // · E5 ✔.
  },

  // ── « REMETS-LA » ────────────────────────────────────────────────────────
  //
  // L'autre sens, et il est du même rang : un geste qu'on ne peut pas défaire
  // depuis la conversation obligerait Dimitri à rouvrir l'écran pour réparer
  // ce que l'opérateur vient de faire.
  data_resa_restaurer: {
    ecran: "donnees",
    endpoint: "/api/beds24-mapping",
    action: "data_resa_restaurer",
    methode: "POST",
    args_autorises: ["logement_id", "mois", "code"],
    // ⚠️ LA RÉSERVATION SE RÉSOUT, ET ELLE POSE SES TROIS ARGUMENTS (lot 6).
    // `code` est la référence de la plateforme (« HMQKTF3F5D ») : Dimitri ne
    // la prononce jamais, et le modèle ne l'a jamais lue. Elle ressemble
    // pourtant à quelque chose qu'on peut inventer — c'est ce qui la rend
    // dangereuse : un code composé retire une AUTRE réservation de la facture
    // du mois. `logement_id` et `mois` sortent du même séjour résolu ; le mois
    // est celui de la FACTURATION (calculé aux nuits), pas celui de l'arrivée.
    args_resolus: {
      code: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
      },
      logement_id: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
        champ: "logement_id",
      },
      mois: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
        champ: "mois",
      },
    },
    apercu: {
      lecture: "data_resa_lire",
      depend_de: ["logement_id", "mois", "code"],
      champ: "retiree",
      jeton: "x-data-version",
    },
    titre: "Remettre une réservation dans la facturation",
    constatable:
      "la réservation cesse d'être barrée dans Argent › Données et recompte dans le total du mois : le montant du propriétaire et la commission remontent d'autant sur sa prochaine facture. Un déplacement de mois resté sur cette réservation est repris au passage, comme le fait le bouton de retour de l'écran. Le geste est refusé si le mois est déjà facturé, si la réservation est déjà comptée, si le logement n'est pas en facturation automatique, ou si le montant de la réservation est réparti sur plusieurs mois — dans ce dernier cas le geste dit que la réservation existe et que sa répartition se règle sur l'écran, il ne prétend jamais qu'elle n'existe pas.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il ne recrée rien et ne ramène aucune correction de montant : il retire seulement la marque qui écartait la réservation du mois.",
    dit_comme: [
      "Remets-la",
      "Finalement elle doit être facturée",
      "Annule le retrait de cette réservation",
    ],
    marquages: ["argent", "sortant"],
    vague: 6,
    motif_requis: false,
    autonome: false,
    sans_retour: null,
  },

  // ── « CHANGE LE NET HÔTE DE CETTE RÉSERVATION » ──────────────────────────
  //
  // Le calque de montant (ADR-211) : la réservation de Beds24 n'est jamais
  // modifiée, on pose une correction à côté, relue à chaque affichage.
  //
  // ⚠️ LE GESTE NE SAIT PAS EFFACER SA PROPRE CORRECTION, ET C'EST VOULU. Le
  // serveur, lui, revient au montant d'origine quand on lui donne une valeur
  // vide — mais un montant omis par distraction effacerait alors une correction
  // en silence, sur le chemin de la facture. La carte exige donc un montant ;
  // revenir à celui de la plateforme se fait sur l'écran, devant le chiffre.
  data_montant_set: {
    ecran: "donnees",
    endpoint: "/api/beds24-mapping",
    action: "data_champ_set",
    methode: "POST",
    args_autorises: ["logement_id", "mois", "code", "nouvelle"],
    // ⚠️ LA RÉSERVATION SE RÉSOUT, ET ELLE POSE SES TROIS ARGUMENTS (lot 6).
    // `code` est la référence de la plateforme (« HMQKTF3F5D ») : Dimitri ne
    // la prononce jamais, et le modèle ne l'a jamais lue. Elle ressemble
    // pourtant à quelque chose qu'on peut inventer — c'est ce qui la rend
    // dangereuse : un code composé retire une AUTRE réservation de la facture
    // du mois. `logement_id` et `mois` sortent du même séjour résolu ; le mois
    // est celui de la FACTURATION (calculé aux nuits), pas celui de l'arrivée.
    args_resolus: {
      code: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
      },
      logement_id: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
        champ: "logement_id",
      },
      mois: {
        objet: "code_resa",
        par: ["logement", "quand", "sens", "rang", "plateforme"],
        champ: "mois",
      },
    },
    // ⚠️ LE NET HÔTE CORRIGÉ SE TAPE, IL NE SE COMPOSE PAS (lot 6, revue
    // adverse M1 — chemin argent). Il était indispensable ; il est désormais
    // une saisie de la carte : retiré s'il est composé, exigé au clic par la
    // carte (bouton gris), posé par `geste-executer` avec ce que Dimitri a
    // tapé. Le paragraphe du dessus reste vrai : sans montant tapé, rien ne
    // part — la carte ne sait toujours pas effacer une correction.
    args_saisis: {
      nouvelle: { libelle: "le net hôte corrigé, en euros", nature: "montant" },
    },
    // Le discriminant n'est pas composé : il est posé. Deux leviers pointent
    // cette action, et confondre le net hôte avec le ménage refacturé
    // changerait la facture sans que rien n'échoue.
    args_imposes: { champ: "montant" },
    apercu: {
      lecture: "data_resa_lire",
      depend_de: ["logement_id", "mois", "code"],
      champ: "montant",
      valeur_arg: "nouvelle",
      jeton: "x-data-version",
    },
    titre: "Corriger le net hôte d'une réservation",
    constatable:
      "la ligne d'Argent › Données affiche le montant corrigé à la place de celui de la plateforme, et le total du mois suit — donc la commission et ce que touche le propriétaire sur sa prochaine facture. La réservation d'origine n'est pas modifiée : la correction vit à côté et se relit à chaque affichage. Le geste est refusé si le mois est déjà facturé, si la réservation est retirée de la facturation, si le logement n'est pas en facturation automatique, ou si le montant de la réservation est réparti sur plusieurs mois — dans ce dernier cas le geste dit que la réservation existe et que sa répartition se règle sur l'écran, il ne prétend jamais qu'elle n'existe pas.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il ne change rien chez Beds24 ni sur les plateformes, ne touche pas au ménage refacturé, et ne sait pas revenir au montant de la plateforme — cela se fait sur l'écran.",
    dit_comme: [
      "Change le net hôte de cette réservation",
      "Change son montant",
      "Corrige le montant de cette réservation pour la facture",
    ],
    marquages: ["argent", "sortant"],
    vague: 6,
    motif_requis: false,
    autonome: false,
    sans_retour:
      "Le montant corrigé remplace le précédent, et l'ancien n'est gardé nulle part : on peut en poser un autre, pas revenir en arrière.",
  },

  // ── « METS UNE REMISE DE 20 EUROS SUR JUILLET » ──────────────────────────
  //
  // Mesuré en production au cadrage du 2026-09-16 : aucun net hôte corrigé,
  // aucun ménage corrigé, et vingt-trois ajustements. C'est LA ligne que
  // Dimitri saisit vraiment — d'où sa place ici, à côté du retrait.
  //
  // ⚠️ L'INTITULÉ EST UN TEXTE QUE DIMITRI RELIT, et il paraît tel quel sur la
  // facture du propriétaire : il arrive donc dans un champ modifiable sur la
  // carte, jamais posé d'un clic. La règle générale enverrait un tel geste en
  // dernière vague ; elle ne s'applique pas ici, et il faut le dire : la
  // dernière vague est OUVERTE en production, l'y ranger aurait ouvert un geste
  // d'argent le jour de sa fusion. Il reste donc avec sa famille, en vague 6,
  // qui vient de toute façon après.
  //
  // ⚠️ LE MONTANT NÉGATIF EST LÉGITIME : une remise, un avoir commercial, un
  // parrainage se saisissent en moins. C'est ce qui rend la lecture d'avant
  // indispensable — sans elle, on ne voit pas ce qui était déjà déduit.
  data_ajustement_ajouter: {
    ecran: "donnees",
    endpoint: "/api/beds24-mapping",
    action: "data_ajustement_ajouter",
    methode: "POST",
    args_autorises: ["logement_id", "mois", "libelle", "montant"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ LE MONTANT SE TAPE, AVEC SON SIGNE (lot 6, M1). Une remise se saisit
    // en moins, un supplément en plus : `montant_signe` accepte les deux et
    // refuse zéro. Sorti des indispensables pour la même raison que partout —
    // une saisie indispensable serait une carte grise à jamais.
    args_indispensables: ["libelle"],
    args_saisis: {
      montant: {
        libelle: "le montant, en moins pour une remise",
        nature: "montant_signe",
      },
    },
    args_textes: ["libelle"],
    apercu: {
      lecture: "data_ajustements_lire",
      depend_de: ["logement_id", "mois"],
      champ: "ajustements",
      valeur_arg: "montant",
      jeton: "x-data-version",
    },
    titre: "Ajouter un ajustement sur un mois",
    constatable:
      "une ligne de plus apparaît sous les réservations du logement dans Argent › Données, avec son intitulé et son montant, et le total du mois s'en trouve modifié — en plus ou en moins selon le signe. Cet intitulé est repris tel quel sur la prochaine facture du propriétaire, d'où le champ modifiable avant le clic. Le geste est refusé si le mois est déjà facturé, en nommant la pièce.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il n'émet aucune facture, ne modifie aucune réservation, et ne remplace jamais un ajustement existant : il en ajoute un.",
    dit_comme: [
      "Ajoute une remise",
      "Ajoute un geste commercial sur ce logement",
      "Il faut déduire 50 euros sur la facture du mois",
    ],
    marquages: ["argent", "sortant"],
    vague: 6,
    motif_requis: false,
    autonome: false,
    sans_retour: null,
  },

  // ── « ENLÈVE CET AJUSTEMENT » ────────────────────────────────────────────
  //
  // ⚠️ LA LIGNE SE CHOISIT, ELLE NE SE COMPOSE PAS. Le modèle ne connaît pas
  // les numéros internes des lignes : le laisser en composer un lui ferait
  // désigner la mauvaise ligne, ou aucune, avec un aplomb égal. La carte
  // demande donc la liste au serveur et Dimitri choisit dedans — la liste et
  // l'écriture suivent le même périmètre, ce mois et ce logement.
  data_ajustement_retirer: {
    ecran: "donnees",
    endpoint: "/api/beds24-mapping",
    action: "data_ajustement_retirer",
    methode: "POST",
    args_autorises: ["logement_id", "mois"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    args_choisis: {
      ajustement_id: {
        lecture: "data_ajustements_choix",
        depend_de: ["logement_id", "mois"],
        etiquette: "Ajustement à enlever",
      },
    },
    apercu: {
      lecture: "data_ajustements_lire",
      depend_de: ["logement_id", "mois"],
      champ: "ajustements",
      jeton: "x-data-version",
    },
    titre: "Enlever un ajustement d'un mois",
    constatable:
      "la ligne choisie disparaît de sous les réservations du logement dans Argent › Données, et le total du mois revient à ce qu'il était sans elle : son montant cesse d'être compté, en plus ou en moins selon son signe — donc ce que touchera le propriétaire sur sa prochaine facture. Le geste est refusé si le mois est déjà facturé, en nommant la pièce.",
    sujet: "argent",
    cout: ["argent"],
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il ne touche à aucune réservation et à aucune autre ligne : seule celle que vous choisissez est enlevée.",
    dit_comme: [
      "Enlève cet ajustement",
      "Retire la remise de juillet",
      "Cette déduction n'a plus lieu d'être",
    ],
    marquages: ["argent", "sortant", "supprime"],
    vague: 6,
    motif_requis: false,
    autonome: false,
    sans_retour:
      "La ligne enlevée n'est pas archivée : il faudra la ressaisir, avec son intitulé et son montant, pour la retrouver.",
  },
});

module.exports = { LEVIERS_ARGENT };
